import React from 'react'
import {
  Archive,
  ArrowLeft,
  Check,
  KeyRound,
  Printer,
  Download,
  ExternalLink,
  Image as ImageGlyph,
  Info,
  Minus,
  TriangleAlert,
  ChevronsUpDown,
  CreditCard,
  FileText,
  Folder,
  LayoutGrid,
  LogOut,
  Mail,
  Menu as MenuGlyph,
  MessageSquare,
  Moon,
  Newspaper,
  Phone,
  Rows3,
  SlidersHorizontal,
  Star,
  Tag,
  Trash2,
  Upload,
  User,
  UserRound,
  Users,
  MapPin,
  Wifi,
  Signal,
  BatteryMedium,
  Video,
  MessageCircle,
  Navigation,
  ShieldCheck,
  Lock,
  CircleAlert,
  RotateCw,
  CheckCheck,
  Ban,
  UserRoundPlus,
  CornerDownLeft,
  ArrowUp,
  LogIn,
  Crown,
  UserX,
  GripVertical,
  Music,
  Film,
  FileSpreadsheet,
  FileArchive,
  Presentation,
  Home,
  FolderPlus,
  Pipette,
  ZoomIn,
  ZoomOut,
  Laptop,
  Monitor,
  Puzzle,
  Receipt,
  UserCog,
  type LucideProps,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Icon registry for the LiveContact design system.
 *
 * `figma` entries are the exact 16px Hugeicons paths exported from the LiveContact Figma file
 * (stroke 1px, currentColor). `lucide` entries are visually-matched fallbacks used until the
 * corresponding Figma asset has been pulled — replace them as screens are implemented.
 */
type FigmaIcon = { kind: 'figma'; paths: React.ReactNode; viewBox?: string; strokeWidth?: number }
type LucideIcon = { kind: 'lucide'; Component: React.ComponentType<LucideProps> }

const P = (d: string, extra: React.SVGProps<SVGPathElement> = {}) => (
  <path d={d} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" {...extra} />
)

const FIGMA: Record<string, FigmaIcon> = {
  bell: {
    kind: 'figma',
    paths: (
      <>
        {P('M10.3335 12C10.3335 13.2887 9.2888 14.3333 8.00013 14.3333C6.71146 14.3333 5.6668 13.2887 5.6668 12')}
        {P('M12.8207 12.0005H3.17925C2.52797 12.0005 2 11.4725 2 10.8213C2 10.5085 2.12424 10.2086 2.34539 9.98739L2.74755 9.58525C3.12262 9.21019 3.33333 8.70145 3.33333 8.17105V6.33385C3.33333 3.75653 5.42267 1.66719 8 1.66719C10.5773 1.66719 12.6667 3.75652 12.6667 6.33385V8.17105C12.6667 8.70145 12.8774 9.21019 13.2525 9.58525L13.6546 9.98739C13.8757 10.2086 14 10.5085 14 10.8213C14 11.4725 13.472 12.0005 12.8207 12.0005Z')}
      </>
    ),
  },
  settings: {
    kind: 'figma',
    paths: (
      <>
        {P('M14.2118 4.76145L13.8827 4.19038C13.6339 3.7585 13.5095 3.54256 13.2977 3.45644C13.086 3.37034 12.8465 3.43828 12.3677 3.57418L11.5542 3.80331C11.2485 3.87382 10.9277 3.83382 10.6485 3.69038L10.4239 3.5608C10.1845 3.40748 10.0004 3.18141 9.89846 2.91568L9.67586 2.25076C9.52946 1.81075 9.45626 1.59074 9.282 1.4649C9.1078 1.33906 8.87633 1.33906 8.4134 1.33906H7.6702C7.20733 1.33906 6.97586 1.33906 6.8016 1.4649C6.62736 1.59074 6.55417 1.81075 6.40779 2.25076L6.18515 2.91568C6.08323 3.18141 5.8991 3.40748 5.65971 3.5608L5.43512 3.69038C5.15596 3.83382 4.83519 3.87382 4.52945 3.80331L3.71596 3.57418C3.23707 3.43828 2.99763 3.37034 2.78591 3.45644C2.57419 3.54256 2.44976 3.7585 2.2009 4.19038L1.87184 4.76145C1.63857 5.16628 1.52193 5.3687 1.54457 5.58418C1.5672 5.79966 1.72335 5.97331 2.03564 6.3206L2.723 7.08906C2.891 7.30172 3.01027 7.67239 3.01027 8.00565C3.01027 8.33905 2.89104 8.70959 2.72302 8.92232L2.03564 9.69079C1.72335 10.0381 1.56721 10.2117 1.54457 10.4273C1.52193 10.6427 1.63857 10.8451 1.87184 11.2499L2.20089 11.821C2.44975 12.2529 2.57419 12.4689 2.78591 12.5549C2.99763 12.6411 3.23708 12.5731 3.71598 12.4372L4.52942 12.2081C4.83522 12.1375 5.15604 12.1776 5.43524 12.3211L5.6598 12.4507C5.89914 12.604 6.08322 12.83 6.18513 13.0957L6.40779 13.7607C6.55417 14.2007 6.62736 14.4207 6.8016 14.5466C6.97586 14.6724 7.20733 14.6724 7.6702 14.6724H8.4134C8.87633 14.6724 9.1078 14.6724 9.282 14.5466C9.45626 14.4207 9.52946 14.2007 9.67586 13.7607L9.89853 13.0957C10.0004 12.83 10.1845 12.604 10.4239 12.4507L10.6484 12.3211C10.9276 12.1776 11.2484 12.1375 11.5542 12.2081L12.3677 12.4372C12.8465 12.5731 13.086 12.6411 13.2977 12.5549C13.5095 12.4689 13.6339 12.2529 13.8827 11.821L14.2118 11.2499C14.4451 10.8451 14.5617 10.6427 14.5391 10.4273C14.5164 10.2117 14.3603 10.0381 14.048 9.69079L13.3606 8.92232C13.1926 8.70959 13.0733 8.33905 13.0733 8.00565C13.0733 7.67239 13.1927 7.30172 13.3606 7.08906L14.048 6.3206C14.3603 5.97331 14.5164 5.79966 14.5391 5.58418C14.5617 5.3687 14.4451 5.16628 14.2118 4.76145Z', { strokeLinejoin: undefined })}
        {P('M10.3463 8.00052C10.3463 9.28919 9.30167 10.3339 8.013 10.3339C6.72433 10.3339 5.67969 9.28919 5.67969 8.00052C5.67969 6.71185 6.72433 5.66719 8.013 5.66719C9.30167 5.66719 10.3463 6.71185 10.3463 8.00052Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
      </>
    ),
  },
  sun: {
    kind: 'figma',
    paths: (
      <>
        {P('M11.3327 8.00052C11.3327 9.84145 9.8403 11.3339 7.99936 11.3339C6.1584 11.3339 4.66602 9.84145 4.66602 8.00052C4.66602 6.15957 6.1584 4.66719 7.99936 4.66719C9.8403 4.66719 11.3327 6.15957 11.3327 8.00052Z')}
        {P('M8.08313 2.16667H7.9998M8.0828 13.8333H7.99947M13.8333 8.08333V8M2.16667 8.08333V8M12.1835 3.93399L12.1245 3.87506M3.93367 12.1833L3.87475 12.1244M12.0658 12.1837L12.1247 12.1248M3.81619 3.93413L3.87511 3.87521')}
      </>
    ),
  },
  search: {
    kind: 'figma',
    paths: (
      <>
        {P('M11.3333 11.3333L14 14')}
        {P('M12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333Z')}
      </>
    ),
  },
  plus: {
    kind: 'figma',
    paths: (
      <>
        {P('M8.00065 3.33333V12.668')}
        {P('M12.668 8.0013H3.33333')}
      </>
    ),
  },
  'filter-lines': {
    kind: 'figma',
    paths: (
      <>
        {P('M2 4H14')}
        {P('M4 8H12')}
        {P('M6 12H10')}
      </>
    ),
  },
  'more-horizontal': {
    kind: 'figma',
    paths: P(
      'M4.00293 8.33333V8M12.0029 8.33333V8M8.00294 8.33333V8M4.6696 8.33333C4.6696 7.96513 4.37112 7.66667 4.00293 7.66667C3.63474 7.66667 3.33626 7.96513 3.33626 8.33333C3.33626 8.70153 3.63474 9 4.00293 9C4.37112 9 4.6696 8.70153 4.6696 8.33333ZM12.6696 8.33333C12.6696 7.96513 12.3711 7.66667 12.0029 7.66667C11.6347 7.66667 11.3363 7.96513 11.3363 8.33333C11.3363 8.70153 11.6347 9 12.0029 9C12.3711 9 12.6696 8.70153 12.6696 8.33333ZM8.6696 8.33333C8.6696 7.96513 8.37114 7.66667 8.00294 7.66667C7.63474 7.66667 7.33627 7.96513 7.33627 8.33333C7.33627 8.70153 7.63474 9 8.00294 9C8.37114 9 8.6696 8.70153 8.6696 8.33333Z'
    ),
  },
  'unfold-more': {
    kind: 'figma',
    paths: (
      <>
        {P('M12 9.33333C12 9.33333 9.05407 12.6667 8 12.6667C6.94587 12.6667 4 9.33333 4 9.33333')}
        {P('M12 6.66664C12 6.66664 9.05407 3.33334 8 3.33333C6.94587 3.33333 4 6.66667 4 6.66667')}
      </>
    ),
  },
  smile: {
    kind: 'figma',
    paths: (
      <>
        {P('M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z')}
        {P('M5.33333 10C5.94141 10.8096 6.90953 11.3333 8 11.3333C9.09047 11.3333 10.0586 10.8096 10.6667 10')}
        {P('M10.4167 5.59133V5.94433M5.58333 5.59133V5.94433')}
      </>
    ),
  },
  attachment: {
    kind: 'figma',
    paths: P(
      'M13 8.00007V9.00007C13 11.7615 10.7614 14.0001 8 14.0001C5.23857 14.0001 3 11.7615 3 9.00007V5.33333C3 3.49239 4.49239 2 6.33333 2C8.17427 2 9.66667 3.49239 9.66667 5.33333V9C9.66667 9.92047 8.92047 10.6667 8 10.6667C7.07953 10.6667 6.33333 9.92047 6.33333 9V6.33333'
    ),
  },
  send: {
    kind: 'figma',
    paths: P(
      'M7.948 3.19336C11.1309 2.1083 12.7223 1.56578 13.5783 2.42174C14.4342 3.27771 13.8917 4.86914 12.8067 8.052L12.0677 10.2195C11.2345 12.6639 10.8178 13.8861 10.1309 13.9872C9.94633 14.0144 9.7552 13.9981 9.57246 13.9394C8.893 13.7213 8.5338 12.4326 7.8154 9.85534C7.65606 9.28367 7.5764 8.9978 7.39493 8.77947C7.34226 8.71614 7.28386 8.65774 7.22053 8.60507C7.0022 8.4236 6.71633 8.34394 6.1447 8.1846C3.5674 7.4662 2.27876 7.107 2.0606 6.42751C2.00194 6.24483 1.98558 6.05369 2.01277 5.86903C2.11392 5.18221 3.33612 4.76556 5.78053 3.93225L7.948 3.19336Z',
      { strokeLinecap: undefined, strokeLinejoin: undefined }
    ),
  },
  'upload-circle': {
    kind: 'figma',
    paths: (
      <>
        {P('M14.6668 7.99967C14.6668 11.6815 11.682 14.6663 8.00016 14.6663C4.31826 14.6663 1.3335 11.6815 1.3335 7.99967C1.3335 4.31777 4.31826 1.33301 8.00016 1.33301C11.682 1.33301 14.6668 4.31777 14.6668 7.99967Z')}
        {P('M10.6667 7.66667C10.6667 7.66667 8.70267 5.00001 8 5C7.29727 4.99999 5.33333 7.66667 5.33333 7.66667M8 5.33333V11')}
      </>
    ),
  },
  'layout-rows': {
    kind: 'figma',
    paths: (
      <>
        {P('M13.4057 2.59449C14.3332 3.52199 14.3332 5.01477 14.3332 8.00033C14.3332 10.9859 14.3332 12.4787 13.4057 13.4062C12.4782 14.3337 10.9854 14.3337 7.99984 14.3337C5.01428 14.3337 3.5215 14.3337 2.594 13.4062C1.6665 12.4787 1.6665 10.9859 1.6665 8.00033C1.6665 5.01477 1.6665 3.52199 2.594 2.59449C3.5215 1.66699 5.01428 1.66699 7.99984 1.66699C10.9854 1.66699 12.4782 1.66699 13.4057 2.59449Z')}
        {P('M14.3333 8H1.66715', { strokeLinejoin: undefined })}
      </>
    ),
  },
  'layout-table': {
    kind: 'figma',
    paths: (
      <>
        {P('M2.594 13.4062C1.6665 12.4787 1.6665 10.9859 1.6665 8.00033C1.6665 5.01477 1.6665 3.52199 2.594 2.59449C3.52149 1.66699 5.01428 1.66699 7.99984 1.66699C10.9854 1.66699 12.4782 1.66699 13.4057 2.59449C14.3332 3.52198 14.3332 5.01477 14.3332 8.00033C14.3332 10.9859 14.3332 12.4787 13.4057 13.4062C12.4782 14.3337 10.9854 14.3337 7.99984 14.3337C5.01428 14.3337 3.5215 14.3337 2.594 13.4062Z')}
        {P('M1.66667 6H14.3333', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M10.6665 14.3337V1.66699', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M5.3335 14.3337V1.66699', { strokeLinecap: undefined, strokeLinejoin: undefined })}
      </>
    ),
  },
  'chevron-down': {
    kind: 'figma',
    paths: P('M12 6.00003C12 6.00003 9.05407 10 8 10C6.94587 10 4 6 4 6'),
  },
  'chevron-left': {
    kind: 'figma',
    paths: P('M10 4C10 4 6.00001 6.94593 6 8C5.99999 9.05413 10 12 10 12'),
  },
  'chevron-right': {
    kind: 'figma',
    paths: P('M6.00003 4C6.00003 4 10 6.94593 10 8C10 9.05413 6 12 6 12'),
  },
  funnel: {
    kind: 'figma',
    paths: P(
      'M5.90497 8.3374C4.24601 7.09707 3.06376 5.73277 2.41823 4.96578C2.2184 4.72835 2.15292 4.5546 2.11355 4.24853C1.97874 3.20053 1.91134 2.67653 2.21863 2.33827C2.52593 2 3.06936 2 4.15622 2H11.8438C12.9307 2 13.4741 2 13.7813 2.33827C14.0887 2.67653 14.0213 3.20053 13.8865 4.24854C13.8471 4.55461 13.7816 4.72836 13.5817 4.96578C12.9353 5.73375 11.7507 7.10047 10.0884 8.34233C9.938 8.45473 9.83887 8.6378 9.82047 8.84093C9.6558 10.6613 9.50393 11.6584 9.4094 12.1628C9.25687 12.9771 8.10213 13.4671 7.484 13.9042C7.11607 14.1644 6.66953 13.8547 6.62185 13.4519C6.53095 12.6841 6.35974 11.1243 6.17285 8.84093C6.15606 8.63593 6.05657 8.45073 5.90497 8.3374Z'
    ),
  },
  eye: {
    kind: 'figma',
    paths: (
      <>
        {P('M14.3628 7.36301C14.5655 7.64721 14.6668 7.78934 14.6668 7.99967C14.6668 8.21001 14.5655 8.35214 14.3628 8.63634C13.4521 9.91341 11.1263 12.6663 8.00016 12.6663C4.87402 12.6663 2.54823 9.91341 1.63752 8.63634C1.43484 8.35214 1.3335 8.21001 1.3335 7.99967C1.3335 7.78934 1.43484 7.64721 1.63752 7.36301C2.54823 6.08597 4.87402 3.33301 8.00016 3.33301C11.1263 3.33301 13.4521 6.08597 14.3628 7.36301Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M10 8C10 6.8954 9.1046 6 8 6C6.8954 6 6 6.8954 6 8C6 9.1046 6.8954 10 8 10C9.1046 10 10 9.1046 10 8Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
      </>
    ),
  },
  close: {
    kind: 'figma',
    paths: P('M12 4L4.00054 11.9995M11.9995 12L4 4.00057'),
  },
  pencil: {
    kind: 'figma',
    viewBox: '0 0 12 12',
    strokeWidth: 0.75,
    paths: (
      <>
        {P('M9.69745 3.78753L6.3678 7.1172C6.114 7.37095 5.7961 7.551 5.44795 7.638L4 8L4.36199 6.55205C4.44902 6.2039 4.62904 5.886 4.88279 5.6322L8.21245 2.30254L8.70745 1.80755C9.11755 1.39748 9.7824 1.39748 10.1924 1.80755C10.6025 2.21762 10.6025 2.88246 10.1924 3.29253L9.69745 3.78753ZM8.21245 2.30254L9.69745 3.78753', { strokeLinecap: undefined })}
        {P('M9.49995 6.75C9.49995 8.39375 9.49995 9.2156 9.046 9.7688C8.9629 9.87005 8.87005 9.9629 8.76875 10.046C8.2156 10.5 7.3937 10.5 5.74995 10.5H5.5C3.61438 10.5 2.67158 10.5 2.08579 9.9142C1.50001 9.32845 1.5 8.3856 1.5 6.5V6.25C1.5 4.60626 1.5 3.78439 1.95397 3.23122C2.03708 3.12995 2.12995 3.03708 2.23122 2.95397C2.78439 2.5 3.60626 2.5 5.25 2.5')}
      </>
    ),
  },
  copy: {
    kind: 'figma',
    viewBox: '0 0 12 12',
    strokeWidth: 0.75,
    paths: (
      <>
        {P('M4.50016 7.49967C4.50016 6.08547 4.50016 5.37837 4.9395 4.93901C5.37886 4.49967 6.08596 4.49967 7.50016 4.49967H8.00016C9.41436 4.49967 10.1215 4.49967 10.5608 4.93901C11.0002 5.37837 11.0002 6.08547 11.0002 7.49967V7.99967C11.0002 9.41387 11.0002 10.121 10.5608 10.5603C10.1215 10.9997 9.41436 10.9997 8.00016 10.9997H7.50016C6.08596 10.9997 5.37886 10.9997 4.9395 10.5603C4.50016 10.121 4.50016 9.41387 4.50016 7.99967V7.49967Z')}
        {P('M8.50031 4.49987C8.49911 3.02132 8.47676 2.25547 8.04636 1.73108C7.96326 1.62981 7.87041 1.53696 7.76916 1.45385C7.21596 0.99987 6.39411 0.99987 4.75036 0.99987C3.10662 0.99987 2.28475 0.99987 1.73157 1.45385C1.6303 1.53695 1.53745 1.62981 1.45434 1.73108C1.00036 2.28426 1.00036 3.10613 1.00036 4.74987C1.00036 6.39362 1.00036 7.21547 1.45434 7.76867C1.53744 7.86992 1.6303 7.96277 1.73157 8.04587C2.25596 8.47627 3.02181 8.49862 4.50036 8.49982')}
      </>
    ),
  },
  calendar: {
    kind: 'figma',
    paths: (
      <>
        {P('M10.6667 1.33333V4M5.33333 1.33333V4')}
        {P('M8.66667 2.66667H7.33333C4.81917 2.66667 3.5621 2.66667 2.78105 3.44771C2 4.22877 2 5.48584 2 8V9.33333C2 11.8475 2 13.1046 2.78105 13.8856C3.5621 14.6667 4.81917 14.6667 7.33333 14.6667H8.66667C11.1808 14.6667 12.4379 14.6667 13.2189 13.8856C14 13.1046 14 11.8475 14 9.33333V8C14 5.48584 14 4.22877 13.2189 3.44771C12.4379 2.66667 11.1808 2.66667 8.66667 2.66667Z')}
        {P('M2 6.66667H14')}
      </>
    ),
  },
  tags: {
    kind: 'figma',
    paths: (
      <>
        {P('M12.0387 5.69097L11.372 5.28191C10.7035 4.87175 10.3693 4.66667 10 4.66667C9.63067 4.66667 9.29647 4.87175 8.628 5.28191L7.96133 5.69097C7.329 6.07899 7.0128 6.27299 6.83973 6.58559C6.66667 6.8982 6.66667 7.27527 6.66667 8.02947V11.9396C6.66667 13.2251 6.66667 13.8679 7.0572 14.2673C7.44773 14.6667 8.07627 14.6667 9.33333 14.6667H10.6667C11.9237 14.6667 12.5523 14.6667 12.9428 14.2673C13.3333 13.8679 13.3333 13.2251 13.3333 11.9396V8.02947C13.3333 7.27527 13.3333 6.8982 13.1603 6.58559C12.9872 6.27299 12.671 6.07899 12.0387 5.69097Z')}
        {P('M9.33333 4.73873C8.90747 4.33007 8.65274 4.11523 8.3316 4.03452C7.95827 3.94068 7.56607 4.04592 6.78167 4.2564L5.99939 4.46631C5.25735 4.66543 4.88633 4.76499 4.62858 5.01087C4.37083 5.25675 4.27095 5.60641 4.07119 6.30571L3.03551 9.9314C2.695 11.1235 2.52475 11.7195 2.81375 12.1891C3.06224 12.5928 4.05567 13.0893 5 13.3333')}
        {P('M9.66313 6.66667C10.224 6.29372 10.7219 5.69527 11.0312 4.95102C11.6699 3.41441 11.2629 1.83816 10.1224 1.43036C8.9818 1.02255 7.5394 1.93763 6.9008 3.47423C6.794 3.73111 6.71647 3.98911 6.66667 4.24255', { strokeLinejoin: undefined })}
      </>
    ),
  },
  clock: {
    kind: 'figma',
    paths: (
      <>
        {P('M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M8 5.33333V8L9.33333 9.33333')}
      </>
    ),
  },
  share: {
    kind: 'figma',
    paths: (
      <>
        {P('M6.26378 3H5.56934C3.60515 3 2.62306 3 2.01286 3.58579C1.40267 4.17157 1.40267 5.11438 1.40267 7V9.66667C1.40267 11.5523 1.40267 12.4951 2.01286 13.0809C2.62306 13.6667 3.60515 13.6667 5.56934 13.6667H8.37376C10.338 13.6667 11.32 13.6667 11.9302 13.0809C12.3256 12.7013 12.4648 12.1719 12.5138 11.3333')}
        {P('M10.7778 4.66667V2.56903C10.7778 2.43886 10.8877 2.33333 11.0233 2.33333C11.0884 2.33333 11.1509 2.35817 11.1969 2.40237L14.3517 5.43097C14.5089 5.58188 14.5972 5.78657 14.5972 6C14.5972 6.21343 14.5089 6.41812 14.3517 6.56903L11.1969 9.5976C11.1509 9.64187 11.0884 9.66667 11.0233 9.66667C10.8877 9.66667 10.7778 9.56113 10.7778 9.43093V7.33333H8.7438C5.91667 7.33333 4.875 9.66667 4.875 9.66667V8C4.875 6.15905 6.42957 4.66667 8.3472 4.66667H10.7778Z')}
      </>
    ),
  },
  nfc: {
    kind: 'figma',
    paths: (
      <>
        {P('M10.3333 2C11.5289 3.19765 12.3333 5.43615 12.3333 8C12.3333 10.5639 11.5289 12.8023 10.3333 14')}
        {P('M8.33333 3.33333C9.1304 4.26484 9.66667 6.0059 9.66667 8C9.66667 9.99413 9.1304 11.7351 8.33333 12.6667')}
        {P('M6 4.66667C6.5978 5.33203 7 6.57564 7 8C7 9.42433 6.5978 10.668 6 11.3333')}
        {P('M3.66667 6C4.0652 6.39922 4.33333 7.1454 4.33333 8C4.33333 8.8546 4.0652 9.6008 3.66667 10')}
      </>
    ),
  },
  link: {
    kind: 'figma',
    paths: (
      <>
        {P('M9 11.3333H11.3333C13.1743 11.3333 14.6667 9.84093 14.6667 8C14.6667 6.15905 13.1743 4.66667 11.3333 4.66667H9M7 11.3333H4.66667C2.82572 11.3333 1.33333 9.84093 1.33333 8C1.33333 6.15905 2.82572 4.66667 4.66667 4.66667H7', { strokeLinejoin: undefined })}
        {P('M6 8H10', { strokeLinejoin: undefined })}
      </>
    ),
  },
  'smartphone-ios': {
    kind: 'figma',
    paths: (
      <>
        {P('M9 1.33333H7C5.42865 1.33333 4.64298 1.33333 4.15482 1.82149C3.66667 2.30965 3.66667 3.09532 3.66667 4.66667V11.3333C3.66667 12.9047 3.66667 13.6903 4.15482 14.1785C4.64298 14.6667 5.42865 14.6667 7 14.6667H9C10.5713 14.6667 11.357 14.6667 11.8452 14.1785C12.3333 13.6903 12.3333 12.9047 12.3333 11.3333V4.66667C12.3333 3.09532 12.3333 2.30965 11.8452 1.82149C11.357 1.33333 10.5713 1.33333 9 1.33333Z')}
        {P('M9.33333 1.33333H6.66667L7 2H9L9.33333 1.33333Z')}
      </>
    ),
  },
  'smartphone-android': {
    kind: 'figma',
    paths: (
      <>
        {P('M9 1.33333H7C5.42865 1.33333 4.64298 1.33333 4.15482 1.82149C3.66667 2.30965 3.66667 3.09532 3.66667 4.66667V11.3333C3.66667 12.9047 3.66667 13.6903 4.15482 14.1785C4.64298 14.6667 5.42865 14.6667 7 14.6667H9C10.5713 14.6667 11.357 14.6667 11.8452 14.1785C12.3333 13.6903 12.3333 12.9047 12.3333 11.3333V4.66667C12.3333 3.09532 12.3333 2.30965 11.8452 1.82149C11.357 1.33333 10.5713 1.33333 9 1.33333Z')}
        {P('M8.08333 12.6667H8M8.16667 12.6667C8.16667 12.7587 8.09207 12.8333 8 12.8333C7.90793 12.8333 7.83333 12.7587 7.83333 12.6667C7.83333 12.5746 7.90793 12.5 8 12.5C8.09207 12.5 8.16667 12.5746 8.16667 12.6667Z')}
      </>
    ),
  },
  'user-outline': {
    kind: 'figma',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: (
      <>
        {P('M17.0003 8.49972C17.0003 5.7383 14.7617 3.49972 12.0003 3.49972C9.23886 3.49972 7.00028 5.7383 7.00028 8.49972C7.00028 11.2611 9.23886 13.4997 12.0003 13.4997C14.7617 13.4997 17.0003 11.2611 17.0003 8.49972Z')}
        {P('M18.9997 20.5C18.9997 16.634 15.8657 13.5 11.9997 13.5C8.13373 13.5 4.99972 16.634 4.99972 20.5')}
      </>
    ),
  },
  'star-filled': {
    kind: 'figma',
    viewBox: '0 0 12 12',
    strokeWidth: 0.75,
    paths: (
      <path
        d="M6.8638 1.72209L7.7437 3.49644C7.8637 3.74343 8.18365 3.98035 8.45365 4.02571L10.0485 4.29287C11.0684 4.46426 11.3083 5.2103 10.5734 5.94625L9.33355 7.19635C9.12355 7.40805 9.0086 7.81635 9.07355 8.10875L9.42855 9.65625C9.7085 10.8811 9.06355 11.355 7.9887 10.7148L6.49385 9.8226C6.2239 9.6613 5.77895 9.6613 5.50395 9.8226L4.00913 10.7148C2.93925 11.355 2.28932 10.8761 2.56929 9.65625L2.92425 8.10875C2.98924 7.81635 2.87426 7.40805 2.66428 7.19635L1.42442 5.94625C0.6945 5.2103 0.929475 4.46426 1.94936 4.29287L3.54418 4.02571C3.80915 3.98035 4.12912 3.74343 4.2491 3.49644L5.129 1.72209C5.60895 0.759304 6.38885 0.759304 6.8638 1.72209Z"
        fill="currentColor"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  globe: {
    kind: 'figma',
    viewBox: '0 0 20 20',
    strokeWidth: 1.11,
    paths: P(
      'M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333M18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667M18.3333 10H1.66667M18.3333 10H13.3333M10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10M10 18.3333C7.86019 16.0865 6.66667 13.1027 6.66667 10M10 18.3333C12.1398 16.0865 13.3333 13.1027 13.3333 10M1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667M1.66667 10H6.66667M10 1.66667C7.86019 3.91346 6.66667 6.89728 6.66667 10M10 1.66667C12.1398 3.91346 13.3333 6.89728 13.3333 10M6.66667 10H13.3333'
    ),
  },
  sparkles: {
    kind: 'figma',
    viewBox: '5 5 18 18',
    strokeWidth: 0.5,
    paths: (
      <>
        <path d="M16.3333 5.5L16.7823 7.49298C17.1631 9.18346 18.4832 10.5036 20.1737 10.8844L22.1667 11.3333L20.1737 11.7823C18.4832 12.1631 17.1631 13.4832 16.7823 15.1737L16.3333 17.1667L15.8844 15.1737C15.5036 13.4832 14.1835 12.1631 12.493 11.7823L10.5 11.3333L12.493 10.8844C14.1834 10.5036 15.5036 9.18346 15.8844 7.49298L16.3333 5.5Z" fill="currentColor" />
        <path d="M9.66667 13.8333L9.98734 15.2569C10.2593 16.4643 11.2023 17.4073 12.4098 17.6793L13.8333 18L12.4098 18.3207C11.2023 18.5927 10.2593 19.5356 9.98734 20.7431L9.66667 22.1667L9.34599 20.7431C9.07399 19.5356 8.13104 18.5927 6.92356 18.3207L5.5 18L6.92356 17.6793C8.13104 17.4073 9.07399 16.4644 9.34599 15.2569L9.66667 13.8333Z" fill="currentColor" />
      </>
    ),
  },
  'user-circle': {
    kind: 'figma',
    paths: (
      <>
        {P('M10.3333 6.99935C10.3333 5.71068 9.28867 4.66602 8 4.66602C6.71133 4.66602 5.66667 5.71068 5.66667 6.99935C5.66667 8.28802 6.71133 9.33268 8 9.33268C9.28867 9.33268 10.3333 8.28802 10.3333 6.99935Z')}
        {P('M14.6667 8.00065C14.6667 4.31875 11.6819 1.33398 8 1.33398C4.3181 1.33398 1.33333 4.31875 1.33333 8.00065C1.33333 11.6825 4.3181 14.6673 8 14.6673C11.6819 14.6673 14.6667 11.6825 14.6667 8.00065Z')}
        {P('M12 13.334C12 11.1249 10.2091 9.33398 8 9.33398C5.79086 9.33398 4 11.1249 4 13.334')}
      </>
    ),
  },
  'qr-code': {
    kind: 'figma',
    paths: (
      <>
        {P('M2 4C2 3.05719 2 2.58579 2.29289 2.29289C2.58579 2 3.05719 2 4 2C4.94281 2 5.41421 2 5.70711 2.29289C6 2.58579 6 3.05719 6 4C6 4.94281 6 5.41421 5.70711 5.70711C5.41421 6 4.94281 6 4 6C3.05719 6 2.58579 6 2.29289 5.70711C2 5.41421 2 4.94281 2 4Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M2 12C2 11.0572 2 10.5858 2.29289 10.2929C2.58579 10 3.05719 10 4 10C4.94281 10 5.41421 10 5.70711 10.2929C6 10.5858 6 11.0572 6 12C6 12.9428 6 13.4142 5.70711 13.7071C5.41421 14 4.94281 14 4 14C3.05719 14 2.58579 14 2.29289 13.7071C2 13.4142 2 12.9428 2 12Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M2 8H6')}
        {P('M8 2V5.33333')}
        {P('M10 4C10 3.05719 10 2.58579 10.2929 2.29289C10.5858 2 11.0572 2 12 2C12.9428 2 13.4142 2 13.7071 2.29289C14 2.58579 14 3.05719 14 4C14 4.94281 14 5.41421 13.7071 5.70711C13.4142 6 12.9428 6 12 6C11.0572 6 10.5858 6 10.2929 5.70711C10 5.41421 10 4.94281 10 4Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M14 8H10C9.0572 8 8.5858 8 8.29287 8.29287C8 8.5858 8 9.0572 8 10M8 11.8461V13.6923M10 10V11C10 11.9643 10.5225 12 11.3333 12C11.7015 12 12 12.2985 12 12.6667M10.6667 14H10M12 10C12.9428 10 13.4142 10 13.7071 10.2933C14 10.5866 14 11.0587 14 12.0029C14 12.9471 14 13.4191 13.7071 13.7125C13.4933 13.9265 13.1845 13.9844 12.6667 14', { strokeLinejoin: undefined })}
      </>
    ),
  },
  signature: {
    kind: 'figma',
    paths: (
      <>
        {P('M14.6667 8.42359C12 10.765 11.6186 7.08132 10.2331 7.34499C8.66668 7.64312 7.66668 10.9637 8.66668 10.9637C9.66668 10.9637 8.33334 7.00065 7.00001 8.37105C5.66668 9.74139 5.23958 11.5304 4.15685 10.2023C-0.99999 3.87696 3.33333 -0.765974 5.44216 2.30522C6.77688 4.24903 4.33334 11.3186 1.33334 14.6673')}
        {P('M6 14H12.6667')}
      </>
    ),
  },
  call: {
    kind: 'figma',
    paths: P(
      'M6.10567 3.80811L5.83723 3.20413C5.66171 2.80921 5.57395 2.61175 5.4427 2.46063C5.27821 2.27125 5.06381 2.13192 4.82395 2.05853C4.63255 1.99996 4.41647 1.99996 3.9843 1.99996C3.3521 1.99996 3.036 1.99996 2.77065 2.12149C2.45807 2.26464 2.17579 2.57548 2.06331 2.90036C1.96783 3.17615 1.99519 3.45958 2.04988 4.02643C2.6321 10.0601 5.94004 13.368 11.9737 13.9503C12.5405 14.0049 12.824 14.0323 13.0997 13.9368C13.4247 13.8243 13.7355 13.5421 13.8787 13.2295C14.0001 12.9641 14.0001 12.648 14.0001 12.0158C14.0001 11.5837 14.0001 11.3676 13.9416 11.1762C13.8682 10.9363 13.7289 10.7219 13.5395 10.5574C13.3884 10.4262 13.1909 10.3384 12.796 10.1629L12.192 9.89447C11.7643 9.7044 11.5505 9.60933 11.3332 9.58867C11.1252 9.56887 10.9155 9.59807 10.7209 9.67387C10.5175 9.75307 10.3377 9.90287 9.97813 10.2025C9.62027 10.5007 9.44133 10.6499 9.22267 10.7297C9.0288 10.8006 8.77253 10.8268 8.5684 10.7967C8.33807 10.7628 8.16173 10.6685 7.809 10.4801C6.71167 9.8936 6.10651 9.28847 5.52007 8.19113C5.33159 7.8384 5.23735 7.66207 5.20341 7.43173C5.17332 7.2276 5.19955 6.97133 5.27036 6.77747C5.35024 6.55881 5.49937 6.37987 5.79762 6.02196C6.09724 5.66241 6.24705 5.48264 6.32626 5.27923C6.40206 5.08459 6.43124 4.87489 6.41146 4.66695C6.39079 4.44964 6.29575 4.2358 6.10567 3.80811Z',
      { strokeLinejoin: undefined }
    ),
  },
  'contact-book': {
    kind: 'figma',
    paths: (
      <>
        {P('M3 6.66667C3 4.15251 3 2.89543 3.78105 2.11438C4.5621 1.33333 5.81917 1.33333 8.33333 1.33333H9.33333C11.8475 1.33333 13.1046 1.33333 13.8856 2.11438C14.6667 2.89543 14.6667 4.15251 14.6667 6.66667V9.33333C14.6667 11.8475 14.6667 13.1046 13.8856 13.8856C13.1046 14.6667 11.8475 14.6667 9.33333 14.6667H8.33333C5.81917 14.6667 4.5621 14.6667 3.78105 13.8856C3 13.1046 3 11.8475 3 9.33333V6.66667Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M3 4H1.33333M3 8H1.33333M3 12H1.33333')}
        {P('M10.1835 5.66206C10.1835 6.39842 9.58653 6.99539 8.85019 6.99539C8.11386 6.99539 7.51693 6.39842 7.51693 5.66206C7.51693 4.92571 8.11386 4.32878 8.85019 4.32878C9.58653 4.32878 10.1835 4.92571 10.1835 5.66206Z')}
        {P('M6.21321 10.4775C6.9188 9.39129 8.03927 8.98423 8.84993 8.98503C9.66067 8.98589 10.7483 9.39129 11.4538 10.4775C11.4995 10.5478 11.512 10.6342 11.4709 10.7072C11.3059 10.9998 10.7935 11.5804 10.4235 11.6198C9.99833 11.665 8.88607 11.6714 8.8508 11.6716C8.81547 11.6714 7.66893 11.665 7.24353 11.6198C6.87353 11.5804 6.3612 10.9998 6.19619 10.7072C6.15503 10.6342 6.16759 10.5478 6.21321 10.4775Z')}
      </>
    ),
  },
  'paint-board': {
    kind: 'figma',
    paths: (
      <>
        {P('M14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667C8.56113 14.6667 9.33333 14.7442 9.33333 14C9.33333 13.594 9.12213 13.2808 8.9124 12.9696C8.60547 12.5143 8.30153 12.0635 8.66667 11.3333C9.11113 10.4445 9.85187 10.4445 10.9877 10.4445C11.5556 10.4445 12.2223 10.4445 13 10.3333C14.4007 10.1333 14.6667 9.27227 14.6667 8Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M6.33333 6.66667C6.88562 6.66667 7.33333 6.21895 7.33333 5.66667C7.33333 5.11438 6.88562 4.66667 6.33333 4.66667C5.78105 4.66667 5.33333 5.11438 5.33333 5.66667C5.33333 6.21895 5.78105 6.66667 6.33333 6.66667Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M11 7.33333C11.5523 7.33333 12 6.88562 12 6.33333C12 5.78105 11.5523 5.33333 11 5.33333C10.4477 5.33333 10 5.78105 10 6.33333C10 6.88562 10.4477 7.33333 11 7.33333Z', { strokeLinecap: undefined, strokeLinejoin: undefined })}
        {P('M4.75 10H4.66667M4.83333 10C4.83333 10.0921 4.75871 10.1667 4.66667 10.1667C4.57462 10.1667 4.5 10.0921 4.5 10C4.5 9.90793 4.57462 9.83333 4.66667 9.83333C4.75871 9.83333 4.83333 9.90793 4.83333 10Z')}
      </>
    ),
  },
  at: {
    kind: 'figma',
    paths: P(
      'M10.4 5.60022V8.6002C10.4 9.59433 11.2059 10.4002 12.2 10.4002C13.1941 10.4002 14 9.59433 14 8.6002V8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C9.351 14 10.5977 13.5535 11.6006 12.8M10.4 8.0002C10.4 9.32573 9.32547 10.4002 8 10.4002C6.67453 10.4002 5.6 9.32573 5.6 8.0002C5.6 6.67473 6.67453 5.60022 8 5.60022C9.32547 5.60022 10.4 6.67473 10.4 8.0002Z',
      { strokeLinejoin: undefined }
    ),
  },
  'upload-small': {
    kind: 'figma',
    viewBox: '0 0 12 12',
    strokeWidth: 1,
    paths: (
      <>
        {P('M6.9985 2C7.1789 2 7.35593 2.04879 7.51086 2.14122C7.66578 2.23364 7.79281 2.36625 7.8785 2.525L8.1215 2.975C8.20719 3.13375 8.33422 3.26636 8.48914 3.35878C8.64407 3.45121 8.8211 3.5 9.0015 3.5H10C10.2652 3.5 10.5196 3.60536 10.7071 3.79289C10.8946 3.98043 11 4.23478 11 4.5V9C11 9.26522 10.8946 9.51957 10.7071 9.70711C10.5196 9.89464 10.2652 10 10 10H2C1.73478 10 1.48043 9.89464 1.29289 9.70711C1.10536 9.51957 1 9.26522 1 9V4.5C1 4.23478 1.10536 3.98043 1.29289 3.79289C1.48043 3.60536 1.73478 3.5 2 3.5H2.9985C3.17871 3.50001 3.35557 3.45132 3.51039 3.35908C3.66521 3.26684 3.79222 3.13449 3.878 2.976L4.1225 2.524C4.20828 2.36551 4.33529 2.23316 4.49011 2.14092C4.64493 2.04868 4.82179 1.99999 5.002 2H6.9985Z')}
        {P('M6 8C6.82843 8 7.5 7.32843 7.5 6.5C7.5 5.67157 6.82843 5 6 5C5.17157 5 4.5 5.67157 4.5 6.5C4.5 7.32843 5.17157 8 6 8Z')}
      </>
    ),
  },
  'arrow-right': {
    kind: 'figma',
    viewBox: '0 0 16.4 16.4',
    strokeWidth: 1.1667,
    paths: (
      <>
        {P('M4.11667 8.2H12.2833')}
        {P('M8.2 4.11667L12.2833 8.2L8.2 12.2833')}
      </>
    ),
  },
}

const LUCIDE: Record<string, LucideIcon> = {
  'chevrons-up-down': { kind: 'lucide', Component: ChevronsUpDown },
  check: { kind: 'lucide', Component: Check },
  minus: { kind: 'lucide', Component: Minus },
  alert: { kind: 'lucide', Component: TriangleAlert },
  info: { kind: 'lucide', Component: Info },
  download: { kind: 'lucide', Component: Download },
  upload: { kind: 'lucide', Component: Upload },
  moon: { kind: 'lucide', Component: Moon },
  'message-multiple': { kind: 'lucide', Component: MessageSquare },
  users: { kind: 'lucide', Component: Users },
  user: { kind: 'lucide', Component: User },
  'user-round': { kind: 'lucide', Component: UserRound },
  'credit-card': { kind: 'lucide', Component: CreditCard },
  file: { kind: 'lucide', Component: FileText },
  folder: { kind: 'lucide', Component: Folder },
  newspaper: { kind: 'lucide', Component: Newspaper },
  logout: { kind: 'lucide', Component: LogOut },
  mail: { kind: 'lucide', Component: Mail },
  phone: { kind: 'lucide', Component: Phone },
  menu: { kind: 'lucide', Component: MenuGlyph },
  star: { kind: 'lucide', Component: Star },
  tag: { kind: 'lucide', Component: Tag },
  trash: { kind: 'lucide', Component: Trash2 },
  grid: { kind: 'lucide', Component: LayoutGrid },
  rows: { kind: 'lucide', Component: Rows3 },
  sliders: { kind: 'lucide', Component: SlidersHorizontal },
  image: { kind: 'lucide', Component: ImageGlyph },
  'external-link': { kind: 'lucide', Component: ExternalLink },
  'arrow-left': { kind: 'lucide', Component: ArrowLeft },
  archive: { kind: 'lucide', Component: Archive },
  key: { kind: 'lucide', Component: KeyRound },
  printer: { kind: 'lucide', Component: Printer },
  laptop: { kind: 'lucide', Component: Laptop },
  monitor: { kind: 'lucide', Component: Monitor },
  puzzle: { kind: 'lucide', Component: Puzzle },
  receipt: { kind: 'lucide', Component: Receipt },
  'shield-check': { kind: 'lucide', Component: ShieldCheck },
  'user-cog': { kind: 'lucide', Component: UserCog },
  'grip-vertical': { kind: 'lucide', Component: GripVertical },
  music: { kind: 'lucide', Component: Music },
  film: { kind: 'lucide', Component: Film },
  'file-spreadsheet': { kind: 'lucide', Component: FileSpreadsheet },
  'file-archive': { kind: 'lucide', Component: FileArchive },
  presentation: { kind: 'lucide', Component: Presentation },
  home: { kind: 'lucide', Component: Home },
  'folder-plus': { kind: 'lucide', Component: FolderPlus },
  pipette: { kind: 'lucide', Component: Pipette },
  'zoom-in': { kind: 'lucide', Component: ZoomIn },
  'zoom-out': { kind: 'lucide', Component: ZoomOut },
  'map-pin': { kind: 'lucide', Component: MapPin },
  wifi: { kind: 'lucide', Component: Wifi },
  signal: { kind: 'lucide', Component: Signal },
  battery: { kind: 'lucide', Component: BatteryMedium },
  video: { kind: 'lucide', Component: Video },
  'message-circle': { kind: 'lucide', Component: MessageCircle },
  navigation: { kind: 'lucide', Component: Navigation },
  shield: { kind: 'lucide', Component: ShieldCheck },
  lock: { kind: 'lucide', Component: Lock },
  'alert-circle': { kind: 'lucide', Component: CircleAlert },
  refresh: { kind: 'lucide', Component: RotateCw },
  'check-double': { kind: 'lucide', Component: CheckCheck },
  ban: { kind: 'lucide', Component: Ban },
  'user-plus': { kind: 'lucide', Component: UserRoundPlus },
  'corner-down-left': { kind: 'lucide', Component: CornerDownLeft },
  'arrow-up': { kind: 'lucide', Component: ArrowUp },
  'log-in': { kind: 'lucide', Component: LogIn },
  crown: { kind: 'lucide', Component: Crown },
  'user-x': { kind: 'lucide', Component: UserX },
}

export type LcIconName = keyof typeof FIGMA | keyof typeof LUCIDE

type Props = Omit<React.SVGProps<SVGSVGElement>, 'name'> & {
  name: LcIconName
  size?: number
  strokeWidth?: number
}

/** 16px stroke icon. Colour follows `currentColor`, so style it via the parent's `text-*` class. */
const LcIcon = ({ name, size = 16, strokeWidth, className, ...props }: Props) => {
  const figma = FIGMA[name as string]
  if (figma) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={figma.viewBox ?? '0 0 16 16'}
        fill="none"
        strokeWidth={strokeWidth ?? figma.strokeWidth ?? 1.25}
        className={cn('shrink-0', className)}
        aria-hidden
        {...props}
      >
        {figma.paths}
      </svg>
    )
  }
  const lucide = LUCIDE[name as string]
  if (!lucide) return null
  const { Component } = lucide
  return (
    <Component
      size={size}
      strokeWidth={strokeWidth ?? 1.5}
      absoluteStrokeWidth
      className={cn('shrink-0', className)}
      aria-hidden
      {...(props as LucideProps)}
    />
  )
}

export default LcIcon
